import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Button } from '@/customer/components/ui/button'
import { Input } from '@/customer/components/ui/input'
import { Textarea } from '@/customer/components/ui/textarea'
import { cn } from '@/customer/lib/utils'

/**
 * Compositional wrapper around an input with optional inline or block addons
 * (icons, text, buttons). Ported from shadcn's `input-group` primitive and
 * restyled to match the customer app's chunky `h-12 / border-2` look.
 *
 * Two visual variants mirror the `Input` primitive:
 *
 * - `box` (default) - full poster-outline box.
 * - `underline` - bare bottom-rule, no fill; fits inside secondary
 *   surfaces like an `EditableField` row where a nested box would
 *   compound visual weight.
 */
const inputGroupVariants = cva(
  [
    'group/input-group relative flex w-full min-w-0 items-center transition-colors outline-none',
    'has-disabled:pointer-events-none has-disabled:opacity-50',
    // Block-align addons stack above/below the input.
    'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col',
    // When an inline addon is present, trim the input's own edge padding
    // so the addon provides the visual gutter.
    'has-[>[data-align=inline-end]]:[&>input]:pr-2 has-[>[data-align=inline-start]]:[&>input]:pl-2',
  ].join(' '),
  {
    variants: {
      variant: {
        box: [
          'h-12 rounded-lg border-2 border-foreground bg-background',
          'has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/30',
          'has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-destructive/30',
        ].join(' '),
        underline: [
          'h-10 border-b-2 border-foreground bg-transparent',
          'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
          'has-[[data-slot][aria-invalid=true]]:border-destructive',
        ].join(' '),
      },
    },
    defaultVariants: { variant: 'box' },
  },
)

function InputGroup({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(inputGroupVariants({ variant }), className)}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 text-body-md font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>svg:not([class*='size-'])]:size-5",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.3rem]',
        'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.3rem]',
        'block-start': 'order-first w-full justify-start px-4 pt-3',
        'block-end': 'order-last w-full justify-start px-4 pb-3',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
)

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva('shadow-none', {
  variants: {
    size: {
      xs: 'h-8',
      sm: 'h-9',
      'icon-xs': 'size-8 p-0 has-[>svg]:p-0',
      'icon-sm': 'size-9 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
})

function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset'
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-body-md text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5",
        className,
      )}
      {...props}
    />
  )
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'h-full flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:border-transparent focus-visible:ring-0 disabled:bg-transparent aria-invalid:border-transparent aria-invalid:ring-0',
        className,
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'min-h-16 flex-1 resize-none rounded-none border-0 bg-transparent px-4 py-3 shadow-none ring-0 focus-visible:border-transparent focus-visible:ring-0 disabled:bg-transparent aria-invalid:border-transparent aria-invalid:ring-0',
        className,
      )}
      {...props}
    />
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
}
